import { dateTimeToView, secondToDateSpan } from "helpers/date";
import { paths } from "helpers/paths";
import { reportFilterWrapper, SelectColumnFilter } from "helpers/reactTable";
import { columnName, formatValue, typeSpecificProps } from "helpers/reports";
import sum from "lodash/sum";
import sumBy from "lodash/sumBy";
import moment from "moment";
import React from "react";
import { list as logistsList } from "sources/books/employees";
import { numbersList } from "sources/books/trucks";
import CommonReport from "./commonReport";

export default class InterBidIntervalReport extends CommonReport {
    cellProps = (column) => {
        return {
            Header: column.label,
            accessor: columnName(column),
            Cell: (props) => {
                const { currentBidId } = props.row.values;
                const uniqueId = { [`data-${props.column.id}`.toLowerCase()]: currentBidId };
                switch (props.column.id) {
                    case "carLogistId": {
                        const { subRows } = props.row;
                        const logistName = subRows[0].subRows[0].original?.carLogistDisplayName || "Без логиста";
                        const rowCount = /\(\d+\)$/.test(logistName) ? "" : ` (${subRows.length})`;
                        return `${logistName}${rowCount}`;
                    }
                    case "carId": {
                        const { subRows } = props.row;
                        const { carNumber } = subRows[0].original;
                        return <span data-car-number={carNumber}>{carNumber}</span>;
                    }
                    case "startedAt":
                    case "endedAt": {
                        const key = { [`data-${props.column.id}`.toLowerCase()]: props.value };
                        return <div {...key}>{dateTimeToView(props.value, "DD.MM.YYYY HH:mm (Z)")}</div>;
                    }
                    case "previousBidId":
                    case "currentBidId": {
                        if (props.value === 0) {
                            return null;
                        }
                        const key = { [`data-${props.column.id}`.toLowerCase()]: props.value };
                        return (
                            <a
                                {...key}
                                href={paths.bidShow("bid", props.value)}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {props.value}
                            </a>
                        );
                    }
                }
                return <span {...uniqueId}>{formatValue(props.value, column.clrType)}</span>;
            },
            ...typeSpecificProps(column),
            ...this.columnSpecificProps(column),
        };
    };

    columnSpecificProps = (column) => {
        switch (column.propertyName) {
            case "CarLogistId": {
                return {
                    filter: (rows, _id, filterValue) => {
                        if (!filterValue || !filterValue?.value) {
                            return rows;
                        }
                        const value = filterValue.value;
                        return rows.filter((row) => row.values[filterValue.id] === value);
                    },
                    Filter: reportFilterWrapper((props) => (
                        <SelectColumnFilter
                            {...props}
                            selectProps={{
                                url: logistsList,
                                inputProps: { name: "logistId" },
                                propName: "fullNameOrEmail",
                            }}
                        />
                    )),
                };
            }
            case "CarId": {
                return {
                    filter: (rows, _id, filterValue) => {
                        if (!filterValue || !filterValue?.value) {
                            return rows;
                        }
                        const value = filterValue.value;
                        return rows.filter((row) => row.values[filterValue.id] === value);
                    },
                    Filter: reportFilterWrapper((props) => (
                        <SelectColumnFilter
                            {...props}
                            selectProps={{
                                url: numbersList,
                                inputProps: { name: "carId" },
                                propName: "number",
                            }}
                        />
                    )),
                };
            }
        }
        return {};
    };

    extractColumns = () => {
        const { columns } = this.props.data || {};
        return columns.map((column) => {
            return {
                ...this.cellProps(column),
                Aggregated: ({ value, row }) => {
                    if (!["Duration", "EmptyFactMileageBefore", "StartedAt", "EndedAt"].includes(column.propertyName)) {
                        return null;
                    }
                    switch (column.propertyName) {
                        case "Duration":
                            return <strong>{secondToDateSpan(value)}</strong>;
                        case "EmptyFactMileageBefore":
                            return <strong>{formatValue(value, "double")}</strong>;
                        case "StartedAt": {
                            if (row.depth !== 1 || row.subRows.length !== 1) {
                                return null;
                            }
                            const { startedAt } = row.subRows[0].original;
                            return (
                                <div>
                                    <b>{dateTimeToView(startedAt, "DD.MM.YYYY HH:mm (Z)")}</b>
                                </div>
                            );
                        }
                        case "EndedAt": {
                            if (row.depth !== 1 || row.subRows.length !== 1) {
                                return null;
                            }
                            const { endedAt } = row.subRows[0].original;
                            return (
                                <div>
                                    <b>{dateTimeToView(endedAt, "DD.MM.YYYY HH:mm (Z)")}</b>
                                </div>
                            );
                        }
                        default:
                            return null;
                    }
                },
                aggregate: (leafValues) => {
                    switch (column.propertyName) {
                        case "Duration":
                            return sumBy(leafValues, (item) => moment.duration(item).asSeconds());
                        case "EmptyFactMileageBefore":
                            return sum(leafValues);
                        default:
                            return null;
                    }
                },
            };
        });
    };

    formatData = (data) =>
        data.map((item) =>
            item.currentBidId === null ? { ...item, carLogistId: 0, carLogistDisplayName: "Без заявок" } : item,
        );

    tableExtraProps = () => ({
        withoutFooter: true,
        initialState: {
            groupBy: ["carLogistId", "carId"],
            hiddenColumns: ["carLogistDisplayName", "carNumber"],
        },
        data: this.formatData(this.props.data.data),
        getRowProps: (row) => {
            if (row.isGrouped) {
                return {};
            }
            const { duration } = row.values;
            if (duration && duration.charAt(0) !== "-") {
                return { className: "tr reports__item--red" };
            } else {
                return { className: "tr reports__item--green" };
            }
        },
    });
}
